import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import createCompanyOptionApi from '@/api/post/company/company-option/create-company-option';
import Alert from '@/components/alert/alert-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import OptionsForm from './options-form';
import { processOptionData, processSubmitData } from './options-utils';
import Styled from './options-new-style';

/**
 * OptionsNew Component
 *
 * Handles the creation of new company options. This component provides a form interface
 * for users to create new options for a specific company, with support for duplicating
 * existing options if needed.
 *
 * Features:
 * - Creates new company options via API
 * - Supports option duplication from existing options
 * - Form validation and submission handling
 * - Loading states and success feedback
 * - Navigation after successful creation
 *
 * @returns {JSX.Element} The rendered options creation form
 *
 * @example
 * // Route usage:
 * <Route path="/company-management/view/:id/options/new" component={OptionsNew} />
 */
const OptionsNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [loading, setLoading] = useState(false);
  const [createCompanyOption, { loading: createCompanyOptionLoading }] =
    useMutation(createCompanyOptionApi);

  /**
   * Handles the company option creation process
   *
   * @param {object} submitValues - The processed form data ready for API submission
   * @param {string} submitValues.name - Option name
   * @param {string} submitValues.description - Option description
   * @param {Array} submitValues.variants - Option variants/choices
   * @param {object} submitValues.pricing - Pricing configuration
   */
  const handleCompanyCreate = (submitValues) => {
    createCompanyOption(submitValues, id)
      .then(() => {
        setLoading(false);
        navigate(`/app/company-management/view/${id}/options`);
        Alert({
          icon: 'success',
          text: 'Option created successfully',
        });
      })
      .catch(() => {
        setLoading(false);
        Alert({
          icon: 'error',
          text: 'Failed to create option',
        });
      });
  };

  /**
   * Processes and submits form data
   *
   * This function handles the form submission by:
   * 1. Processing the raw form values into API-ready format
   * 2. Handling any file uploads or data transformations
   * 3. Calling the create API
   *
   * @param {object} values - Raw form values from the form
   * @param {string} values.name - Raw option name
   * @param {string} values.description - Raw option description
   * @param {Array} values.variants - Raw variant data
   * @param {object} values.pricing - Raw pricing data
   */
  const submitForm = async (values) => {
    setLoading(true);
    const submitValues = await processSubmitData(values, 'add');

    handleCompanyCreate(submitValues);
  };

  const isLoading = createCompanyOptionLoading || loading;

  return (
    <Styled.Main>
      <FormWrapper
        defaultValues={async () =>
          // Process initial form data, handling duplication if needed
          processOptionData(id, locationState?.isOptionDuplicate, {
            isEdit: false,
            isDuplicate: !!locationState?.isOptionDuplicate,
          })
        }
        onSubmit={submitForm}
      >
        <OptionsForm
          type="create"
          isLoading={isLoading}
          submitForm={submitForm}
        />
      </FormWrapper>
    </Styled.Main>
  );
};

export default OptionsNew;
